/**
 * Column configuration types for the DocumentComparisonLayout.
 */

import type { ReactNode } from 'react';
import type { Citation, ConfidenceScores } from '../types';

// ============================================================================
// Base Column Configuration
// ============================================================================

/**
 * Base configuration shared by all column types.
 */
export interface BaseColumnConfig {
  /** Unique identifier for the column */
  id: string;
  /** Title displayed in the column header */
  title: string;
  /** Minimum width in pixels */
  minWidthPx?: number;
  /** Default width as percentage of total (columns should sum to 100) */
  defaultWidthPercent?: number;
}

// ============================================================================
// Document Column
// ============================================================================

/**
 * Configuration for a document viewer column.
 */
export interface DocumentColumnConfig extends BaseColumnConfig {
  type: 'document';
  /** URL to the document (PDF or image) */
  documentUrl: string;
  /** Type of document */
  documentType: 'pdf' | 'image';
  /** MIME type of the document */
  mimeType?: string;
  /** All citations for this document */
  citations: Citation[];
  /** Map from citation to field path (for reverse lookup on hover) */
  citationFieldMap: Map<Citation, string>;
}

// ============================================================================
// Data Column
// ============================================================================

/**
 * Context provided to data column render function.
 */
export interface DataColumnRenderContext {
  /** Currently hovered field path */
  hoveredFieldPath: string | null;
  /** Currently locked field path */
  lockedFieldPath: string | null;
  /** Call when hovering a field */
  onFieldHover: (path: string | null) => void;
  /** Call when clicking a field (to lock) */
  onFieldClick: (path: string) => void;
  /** Get citations for a specific field */
  getCitationsForField: (path: string) => Citation[];
  /** Get confidence scores for a specific field */
  getConfidenceForField: (path: string) => ConfidenceScores | null;
}

/**
 * Configuration for a data display column.
 */
export interface DataColumnConfig extends BaseColumnConfig {
  type: 'data';
  /** Render function for the column content */
  render: (context: DataColumnRenderContext) => ReactNode;
}

// ============================================================================
// Custom Column
// ============================================================================

/**
 * Configuration for a custom column (fully user-controlled).
 */
export interface CustomColumnConfig extends BaseColumnConfig {
  type: 'custom';
  /** Render function for the column content */
  render: () => ReactNode;
}

// ============================================================================
// Union Type
// ============================================================================

/**
 * Any column configuration type.
 */
export type ColumnConfig =
  | DocumentColumnConfig
  | DataColumnConfig
  | CustomColumnConfig;

/**
 * Type guard for document columns.
 */
export function isDocumentColumn(
  config: ColumnConfig,
): config is DocumentColumnConfig {
  return config.type === 'document';
}

/**
 * Type guard for data columns.
 */
export function isDataColumn(config: ColumnConfig): config is DataColumnConfig {
  return config.type === 'data';
}

/**
 * Type guard for custom columns.
 */
export function isCustomColumn(
  config: ColumnConfig,
): config is CustomColumnConfig {
  return config.type === 'custom';
}
